import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../sections/Footer/Footer';
import SEO from '../components/SEO';
import { useTranslation } from '../i18n/useTranslation';
import { getBlogPostBySlug } from '../data/blogPosts';
import PageTitle from '../components/PageTitle/PageTitle';

const BlogPost = ({ lang = 'en' }) => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  
  const BASE_URL = "https://alsaketdental.com";
  const SITE_NAME = "AlSaket Dental Clinics";
  const SITE_NAME_AR = "عيادات الساكت لطب الأسنان";
  
  const post = getBlogPostBySlug(slug, lang);
  
  if (!post) {
    // Post not found, redirect to home
    window.location.href = `/${lang}`;
    return null;
  }
  
  const isArabic = lang === 'ar';
  const pageUrl = `${BASE_URL}/${lang}/blog/${slug}`;
  
  // Generate content based on keyword
  const generateContent = (post, isAr) => {
    if (isAr) {
      return {
        intro: `تعرف على خدمات ${post.keywordAr} المتخصصة في عيادات الساكت لطب الأسنان في عمان. الدكتور يوسف الساكت يقدم رعاية أسنان متقدمة باستخدام أحدث التقنيات.`,
        body: `في عيادات الساكت لطب الأسنان، نقدم خدمات ${post.keywordAr} بأعلى معايير الجودة. الدكتور يوسف الساكت حاصل على درجة دكتور جراحة الأسنان (DDS) من الجامعة الأردنية وعضوية كلية طب الأسنان في الكلية الملكية للجراحين في أيرلندا (MFD RCSI)، بالإضافة إلى زمالة في طب الأسنان التجميلي والترميمي من جامعة جنوة، إيطاليا.`,
        conclusion: `احجز موعدك الآن مع الدكتور يوسف الساكت للحصول على أفضل خدمات ${post.keywordAr} في عمان. نستخدم أحدث المعدات والتقنيات لضمان أفضل النتائج.`
      };
    } else {
      return {
        intro: `Discover expert ${post.keyword} services at AlSaket Dental Clinics in Amman. Dr. Yousef AlSaket provides advanced dental care using the latest technology and techniques.`,
        body: `At AlSaket Dental Clinics, we offer ${post.keyword} services with the highest quality standards. Dr. Yousef AlSaket earned his Doctor of Dental Surgery (DDS) degree from the University of Jordan and his Membership of the Faculty of Dentistry at the Royal College of Surgeons in Ireland (MFD RCSI), plus a fellowship in Aesthetic and Restorative Dentistry from the University of Genoa, Italy.`,
        conclusion: `Book your appointment now with Dr. Yousef AlSaket for the best ${post.keyword} services in Amman. We use the latest equipment and techniques to ensure optimal results.`
      };
    }
  };
  
  const content = generateContent(post, isArabic);
  
  // JSON-LD for Blog Posting
  const blogPostLD = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": isArabic ? post.titleAr : post.title,
    "alternateHeadline": isArabic ? post.title : post.titleAr,
    "description": isArabic ? post.descriptionAr : post.description,
    "author": {
      "@type": "Person",
      "name": "Dr. Yousef AlSaket",
      "alternateName": "الدكتور يوسف الساكت"
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "alternateName": SITE_NAME_AR,
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/images/logo.png`
      }
    },
    "datePublished": new Date().toISOString().split('T')[0],
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": pageUrl
    },
    "articleBody": `${content.intro} ${content.body} ${content.conclusion}`,
    "keywords": isArabic ? post.keywordsAr.join(", ") : post.keywords.join(", "),
    "inLanguage": lang
  };
  
  return (
    <>
      <SEO
        title={isArabic ? post.titleAr : post.title}
        titleAr={isArabic ? post.title : post.titleAr}
        description={isArabic ? (post.descriptionAr || post.description) : (post.description || post.descriptionAr)}
        descriptionAr={isArabic ? (post.description || post.descriptionAr) : (post.descriptionAr || post.description)}
        keywords={post.keywords || []}
        keywordsAr={post.keywordsAr || []}
        url={pageUrl}
        image={`${BASE_URL}/images/blog/${post.slug}.jpg`}
        siteName={SITE_NAME}
        siteNameAr={SITE_NAME_AR}
        structuredData={[blogPostLD]}
        lang={isArabic ? 'ar' : 'en'}
      />
      
      <Navbar />
      
      <section className='section-bg section-common blog-section pt-100 pb-70'>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <PageTitle 
                title={isArabic ? post.titleAr : post.title}
                description={isArabic ? "مقال طبي متخصص" : "Expert Dental Article"}
              />
              
              <article className='blog-content' style={{ 
                maxWidth: '800px', 
                margin: '0 auto',
                textAlign: lang === 'ar' ? 'right' : 'left',
                direction: lang === 'ar' ? 'rtl' : 'ltr'
              }}>
                <div className="blog-meta" style={{ 
                  marginBottom: '30px',
                  paddingBottom: '20px',
                  borderBottom: '2px solid #DBEFFA'
                }}>
                  <p style={{ 
                    fontSize: '14px', 
                    color: '#666',
                    marginBottom: '10px'
                  }}>
                    {isArabic ? 'بواسطة: الدكتور يوسف الساكت' : 'By: Dr. Yousef AlSaket'}
                  </p>
                  <p style={{ 
                    fontSize: '14px', 
                    color: '#666'
                  }}>
                    {new Date().toLocaleDateString(lang === 'ar' ? 'ar-JO' : 'en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
                
                <div className="blog-body" style={{ 
                  fontSize: '18px',
                  lineHeight: '1.8',
                  color: '#333'
                }}>
                  <p style={{ marginBottom: '20px', fontWeight: '500' }}>
                    {content.intro}
                  </p>
                  
                  <p style={{ marginBottom: '20px' }}>
                    {content.body}
                  </p>
                  
                  <p style={{ marginBottom: '20px' }}>
                    {isArabic ? 
                      'نقدم خدمات شاملة لطب الأسنان تشمل الفحوصات الدورية، الوقاية، والعلاج. عياداتنا مجهزة بأحدث المعدات والتقنيات لضمان راحة المرضى ونتائج متميزة.' :
                      'We provide comprehensive dental services including routine examinations, prevention, and treatment. Our clinics are equipped with the latest equipment and techniques to ensure patient comfort and excellent results.'
                    }
                  </p>
                  
                  <p style={{ marginBottom: '20px' }}>
                    {isArabic ?
                      'الدكتور يوسف الساكت متخصص في تقديم رعاية أسنان عالية الجودة باستخدام أحدث الأساليب والمعدات الطبية. خبرته الواسعة في مجال طب الأسنان تسمح له بتقديم حلول مخصصة لكل مريض.' :
                      'Dr. Yousef AlSaket specializes in providing high-quality dental care using the latest methods and medical equipment. His extensive experience in dentistry allows him to provide customized solutions for each patient.'
                    }
                  </p>
                  
                  <p style={{ marginBottom: '20px' }}>
                    {content.conclusion}
                  </p>
                  
                  <div className="blog-cta" style={{
                    marginTop: '40px',
                    padding: '30px',
                    backgroundColor: '#DBEFFA',
                    borderRadius: '15px',
                    textAlign: 'center'
                  }}>
                    <h3 style={{ marginBottom: '20px' }}>
                      {isArabic ? 'احجز موعدك الآن' : 'Book Your Appointment Now'}
                    </h3>
                    <p style={{ marginBottom: '20px' }}>
                      {isArabic ?
                        'تواصل معنا لحجز موعدك مع الدكتور يوسف الساكت للحصول على أفضل خدمات طب الأسنان في عمان.' :
                        'Contact us to book your appointment with Dr. Yousef AlSaket for the best dental services in Amman.'
                      }
                    </p>
                    <a 
                      href={`/${lang}/contact`}
                      className="theme-btn"
                      style={{
                        display: 'inline-block',
                        marginTop: '10px'
                      }}
                    >
                      {isArabic ? 'احجز موعد' : 'Book Appointment'}
                    </a>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </>
  );
};

export default BlogPost;

